import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, ShieldCheck, Info } from "lucide-react";

export default function ChuckConsentPanel() {
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/chuck/consent");
      setState(data);
    } catch (e) {}
  };
  useEffect(() => { load(); }, []);

  const toggle = async () => {
    if (!state) return;
    setSaving(true);
    try {
      const { data } = await api.post("/chuck/consent", { consent: !state.consent });
      setState((s) => ({ ...s, consent: data.consent }));
      toast.success(data.consent ? "Thanks — Chuck is now learning from your work." : "Consent turned off.");
    } catch (e) {
      toast.error("Could not update consent");
    } finally { setSaving(false); }
  };

  if (!state) return null;
  const isEnterprise = state.plan === "enterprise";

  return (
    <div className="border border-slate-200 bg-white p-6" data-testid="chuck-consent-panel">
      <div className="flex items-start gap-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center border border-[#2563EB]/40 bg-[#2563EB]/10">
          <Sparkles className="h-5 w-5 text-[#93C5FD]" />
        </div>
        <div className="flex-1">
          <div className="overline text-[#93C5FD]">HELP TRAIN CHUCK</div>
          <h3 className="mt-1 font-serif text-xl font-semibold text-slate-900">
            Qualmitta's own regulatory model
          </h3>
          <p className="mt-2 max-w-xl text-sm text-slate-500">
            When you're logged in and this is on, your edits, corrections, and thumbs
            up/down feedback are used — de-identified — to train Chuck, our own model
            specialized for FDA workflows. We never store PHI, never train on Claude's
            output, and never touch Enterprise data.{" "}
            <a href="https://www.qualmitta.com/privacy" className="text-[#93C5FD] hover:underline">
              See how it works
            </a>
            .
          </p>
          <div className="mt-4 flex items-center gap-3">
            {isEnterprise ? (
              <div className="inline-flex items-center gap-2 border border-emerald-400/40 bg-emerald-500/10 px-3 py-1.5 text-xs text-emerald-200">
                <ShieldCheck className="h-3.5 w-3.5" />
                Enterprise plan — your data is <b className="mx-1">never</b> used for training.
              </div>
            ) : (
              <button
                onClick={toggle}
                disabled={saving}
                data-testid="chuck-consent-toggle"
                className={`inline-flex items-center gap-2 border px-4 py-2 text-xs font-semibold transition-all disabled:opacity-50 ${
                  state.consent
                    ? "border-emerald-400/50 bg-emerald-500/15 text-emerald-200"
                    : "border-slate-300 text-slate-600 hover:border-[#2563EB]"
                }`}
              >
                {state.consent ? "✓ Chuck is learning from your work" : "Turn on Chuck learning"}
              </button>
            )}
            <div className="text-xs text-slate-400">
              {state.events_recorded} events · {state.plan.toUpperCase()} plan
            </div>
          </div>
        </div>
      </div>
      <div className="mt-5 flex items-start gap-2 border-t border-slate-200 pt-4 text-[11px] text-slate-400">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <div>
          Everything is stored append-only, de-identified, and never leaves Qualmitta.
          You can turn this off any time, and you can request a full deletion any time
          under Data & Privacy.
        </div>
      </div>
    </div>
  );
}
